import { AddressType } from '../../../store/reducers/GetRates/Type';
import { RatesInputType, BoxType } from '../Type';

export interface AISavedLocation {
  id: string;
  name: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  addressLine: string;
}

export const SAVED_SHIPPERS: AISavedLocation[] = [
  {
    id: 's1',
    name: 'Delhi Warehouse (Aerocity)',
    city: 'Delhi',
    state: 'Delhi',
    country: 'India',
    pincode: '110037',
    addressLine: 'Plot 12, Cargo Area, Aerocity, New Delhi',
  },
  {
    id: 's2',
    name: 'Mumbai Central Hub',
    city: 'Mumbai',
    state: 'Maharashtra',
    country: 'India',
    pincode: '400099',
    addressLine: 'Sahar Cargo Complex, Andheri East, Mumbai',
  },
  {
    id: 's3',
    name: 'Bangalore Fulfillment Center',
    city: 'Bangalore',
    state: 'Karnataka',
    country: 'India',
    pincode: '560001',
    addressLine: '45, MG Road, Shivaji Nagar, Bengaluru',
  },
];

export const SAVED_CONSIGNEES: AISavedLocation[] = [
  {
    id: 'c1',
    name: 'London Corporate Office',
    city: 'London',
    state: 'London',
    country: 'United Kingdom',
    pincode: 'EC1A 1BB',
    addressLine: '100 St John Street, Clerkenwell, London',
  },
  {
    id: 'c2',
    name: 'New York Gateway Depot',
    city: 'New York',
    state: 'New York',
    country: 'United States',
    pincode: '10001',
    addressLine: '350 5th Ave (Empire State Building), New York',
  },
  {
    id: 'c3',
    name: 'Mumbai Office (Fort)',
    city: 'Mumbai',
    state: 'Maharashtra',
    country: 'India',
    pincode: '400001',
    addressLine: '12, Fort Street, Chhatrapati Shivaji Terminus Area, Mumbai',
  },
];

// Mock database for pincode matching validation
const CITY_PINCODE_PATTERNS: Record<string, { regex: RegExp; suggest: string; fullName: string }> = {
  mumbai: { regex: /^400\d{3}$/, suggest: '400001', fullName: 'Mumbai' },
  delhi: { regex: /^110\d{3}$/, suggest: '110001', fullName: 'Delhi' },
  bangalore: { regex: /^560\d{3}$/, suggest: '560001', fullName: 'Bangalore' },
  bengaluru: { regex: /^560\d{3}$/, suggest: '560001', fullName: 'Bangalore' },
  chennai: { regex: /^600\d{3}$/, suggest: '600001', fullName: 'Chennai' },
  kolkata: { regex: /^700\d{3}$/, suggest: '700001', fullName: 'Kolkata' },
};

/**
 * Validates a pincode against city name and returns a recommendation if mismatch is found
 */
export function validatePincode(pincode: string, city: string): { valid: boolean; suggestion?: string } {
  if (!pincode || !city) return { valid: true };
  const cleanCity = city.toLowerCase().trim();
  const cleanPincode = pincode.replace(/\s+/g, '');

  const pattern = CITY_PINCODE_PATTERNS[cleanCity];
  if (pattern) {
    const isValid = pattern.regex.test(cleanPincode);
    if (!isValid) {
      return {
        valid: false,
        suggestion: pattern.suggest,
      };
    }
  }
  return { valid: true };
}

/**
 * Checks weight consistency for commodities to prevent typos
 */
export function checkWeightConsistency(
  weight: number,
  quantity: number,
  description: string,
): { valid: boolean; status: 'ok' | 'warning'; message?: string } {
  if (!weight || weight <= 0) return { valid: true, status: 'ok' };
  const cleanDesc = description.toLowerCase();

  // Rules:
  // 1. Documents shouldn't be very heavy
  if ((cleanDesc.includes('document') || cleanDesc.includes('paper') || cleanDesc.includes('passport')) && weight > 5) {
    return {
      valid: false,
      status: 'warning',
      message: `The declared weight of ${weight} kg seems unusually high for documents. Please verify.`,
    };
  }

  // 2. Heavy shipments warning
  if (weight > 2000) {
    return {
      valid: false,
      status: 'warning',
      message: `The declared weight of ${weight} kg is very high. Make sure it's correct for ${quantity} items.`,
    };
  }

  // 3. Low weight warning
  if (weight < 0.1) {
    return {
      valid: false,
      status: 'warning',
      message: `The declared weight of ${weight} kg is extremely light. Please check if unit of measurement is correct.`,
    };
  }

  return { valid: true, status: 'ok' };
}

/**
 * Parses free-text shipment query using rule-based heuristics
 */
export function extractShipmentFromText(
  text: string,
): Partial<RatesInputType & { boxes: BoxType[]; hideBoxes: boolean }> {
  if (!text) return {};

  const cleanText = text.toLowerCase();

  // 1. Identify Shipment Type
  let shipmentType = 'parcel';
  if (
    cleanText.includes('document') ||
    cleanText.includes('paper') ||
    cleanText.includes('letter') ||
    cleanText.includes('passport')
  ) {
    shipmentType = 'document';
  }

  // 2. Identify Locations (Shipper & Consignee)
  let pickupCity = '';
  let destCity = '';

  const cityKeywords = ['delhi', 'mumbai', 'bangalore', 'bengaluru', 'chennai', 'kolkata', 'london', 'new york'];

  // Find cities using context clues (from City A to City B)
  const fromMatches = text.match(
    /(?:from|pickup\s*at|origin\s*in)\s+([a-zA-Z\s]+?)(?=\s+to|\s+for|\s+and|\s+with|\s+at|\s+\d|\.|,|$)/i,
  );
  const toMatches = text.match(
    /(?:to|destination|delivery\s*to)\s+([a-zA-Z\s]+?)(?=\s+from|\s+for|\s+and|\s+with|\s+at|\s+\d|\.|,|$)/i,
  );

  if (fromMatches && fromMatches[1]) {
    const candidate = fromMatches[1].trim().toLowerCase();
    const found = cityKeywords.find(c => candidate.includes(c));
    if (found) pickupCity = found;
  }
  if (toMatches && toMatches[1]) {
    const candidate = toMatches[1].trim().toLowerCase();
    const found = cityKeywords.find(c => candidate.includes(c));
    if (found) destCity = found;
  }

  // Fallback: search anywhere in text if pattern match failed
  if (!pickupCity || !destCity) {
    const foundCities = cityKeywords.filter(c => cleanText.includes(c));
    if (foundCities.length >= 2) {
      // Assume first mentioned is pickup, second is destination
      const idx1 = cleanText.indexOf(foundCities[0]);
      const idx2 = cleanText.indexOf(foundCities[1]);
      if (idx1 < idx2) {
        pickupCity = pickupCity || foundCities[0];
        destCity = destCity || foundCities[1];
      } else {
        pickupCity = pickupCity || foundCities[1];
        destCity = destCity || foundCities[0];
      }
    } else if (foundCities.length === 1) {
      if (cleanText.indexOf('to') > cleanText.indexOf(foundCities[0])) {
        pickupCity = pickupCity || foundCities[0];
      } else {
        destCity = destCity || foundCities[0];
      }
    }
  }

  // Map to Saved Address Objects
  const shipperAddr = SAVED_SHIPPERS.find(s => s.city.toLowerCase() === pickupCity) || {
    city: pickupCity ? pickupCity.charAt(0).toUpperCase() + pickupCity.slice(1) : '',
    state: pickupCity
      ? pickupCity === 'delhi'
        ? 'Delhi'
        : pickupCity === 'mumbai'
        ? 'Maharashtra'
        : pickupCity === 'bangalore' || pickupCity === 'bengaluru'
        ? 'Karnataka'
        : ''
      : '',
    country: 'India',
    pincode: pickupCity ? CITY_PINCODE_PATTERNS[pickupCity]?.suggest || '' : '',
  };

  const consigneeAddr = SAVED_CONSIGNEES.find(c => c.city.toLowerCase() === destCity) ||
    SAVED_SHIPPERS.find(s => s.city.toLowerCase() === destCity) || {
      city: destCity ? destCity.charAt(0).toUpperCase() + destCity.slice(1) : '',
      state: destCity
        ? destCity === 'delhi'
          ? 'Delhi'
          : destCity === 'mumbai'
          ? 'Maharashtra'
          : destCity === 'bangalore' || destCity === 'bengaluru'
          ? 'Karnataka'
          : ''
        : '',
      country: destCity === 'london' ? 'United Kingdom' : destCity === 'new york' ? 'United States' : 'India',
      pincode: destCity ? CITY_PINCODE_PATTERNS[destCity]?.suggest || '' : '',
    };

  // 3. Extract weight and weight unit
  let weight = 0;
  let weightUnit: 'kg' | 'lb' = 'kg';

  const weightRegex = /(\d+(?:\.\d+)?)\s*(kg|lb|kilo|kilogram|pound|lbs)/gi;
  const weightMatches = weightRegex.exec(cleanText);
  if (weightMatches) {
    weight = parseFloat(weightMatches[1]);
    const unitStr = weightMatches[2].toLowerCase();
    if (unitStr.includes('lb') || unitStr.includes('pound')) {
      weightUnit = 'lb';
    }
  } else {
    // Look for plain number near "weight is X"
    const generalWeightRegex = /(?:weight|weighs|mass)(?:\s*(?:is|of))?\s*(\d+(?:\.\d+)?)/gi;
    const generalMatches = generalWeightRegex.exec(cleanText);
    if (generalMatches) {
      weight = parseFloat(generalMatches[1]);
    }
  }

  // 4. Extract boxes count
  let boxCount = 1;
  const countRegex = /(\d+)\s*(box|boxes|package|packages|carton|cartons|parcel|parcels|item|items)/gi;
  const countMatches = countRegex.exec(cleanText);
  if (countMatches) {
    boxCount = parseInt(countMatches[1]);
  }

  // 5. Extract dimensions (e.g. 40x30x20 or 40 x 30 x 20 cm)
  let length = 10;
  let width = 10;
  let height = 10;
  let hasDim = false;

  const dimRegex = /(\d+)\s*x\s*(\d+)\s*x\s*(\d+)/gi;
  const dimMatches = dimRegex.exec(cleanText);
  if (dimMatches) {
    length = parseInt(dimMatches[1]);
    width = parseInt(dimMatches[2]);
    height = parseInt(dimMatches[3]);
    hasDim = true;
  }

  // 6. Extract product description / commodity
  let commodity = '';
  const commodities = [
    'electronics',
    'documents',
    'books',
    'clothes',
    'garments',
    'medicines',
    'spares',
    'parts',
    'toys',
    'cosmetics',
  ];
  for (const c of commodities) {
    if (cleanText.includes(c)) {
      commodity = c.charAt(0).toUpperCase() + c.slice(1);
      break;
    }
  }

  // 7. Create Box Objects
  const boxes: BoxType[] = [];
  if (shipmentType === 'parcel') {
    const singleBoxWeight = weight > 0 ? weight / boxCount : 5;
    for (let i = 0; i < boxCount; i++) {
      boxes.push({
        id: i.toString(),
        weight: parseFloat(singleBoxWeight.toFixed(2)),
        length: hasDim ? length : 30,
        width: hasDim ? width : 30,
        height: hasDim ? height : 20,
        boxCopyCount: 0,
        startCount: i + 1,
        // Custom attribute parsed by AI
        description: commodity || `Box ${i + 1} - Goods`,
      } as any);
    }
  }

  return {
    shipmentType,
    pickup: shipperAddr as AddressType,
    destination: consigneeAddr as AddressType,
    pickupPinCode: shipperAddr.pincode,
    destinationPinCode: consigneeAddr.pincode,
    weight: weight > 0 ? weight : shipmentType === 'document' ? 0.5 : 10,
    weightUnit,
    boxes,
    hideBoxes: boxes.length === 0,
  };
}

/**
 * Simulates AI Invoice Information Extraction
 */
export function extractShipmentFromInvoice(
  fileName: string,
): Promise<Partial<RatesInputType & { boxes: BoxType[]; hideBoxes: boolean }>> {
  return new Promise(resolve => {
    setTimeout(() => {
      // Extracted mock info depending on name keywords
      const lowerName = fileName.toLowerCase();
      let extracted: Partial<RatesInputType & { boxes: BoxType[]; hideBoxes: boolean }> = {};

      if (lowerName.includes('tech') || lowerName.includes('electronic') || lowerName.includes('invoice_1')) {
        extracted = {
          shipmentType: 'parcel',
          pickup: SAVED_SHIPPERS[0] as AddressType,
          destination: SAVED_CONSIGNEES[1] as AddressType,
          pickupPinCode: SAVED_SHIPPERS[0].pincode,
          destinationPinCode: SAVED_CONSIGNEES[1].pincode,
          weight: 45,
          weightUnit: 'kg',
          hideBoxes: false,
          boxes: [
            {
              id: '0',
              weight: 15,
              length: 40,
              width: 30,
              height: 25,
              boxCopyCount: 0,
              startCount: 1,
              description: 'Laptops and Adapters',
            },
            {
              id: '1',
              weight: 15,
              length: 40,
              width: 30,
              height: 25,
              boxCopyCount: 0,
              startCount: 2,
              description: 'LED Screens',
            },
            {
              id: '2',
              weight: 15,
              length: 40,
              width: 30,
              height: 25,
              boxCopyCount: 0,
              startCount: 3,
              description: 'Power banks and cabling',
            },
          ] as any,
        };
      } else if (lowerName.includes('doc') || lowerName.includes('agreement') || lowerName.includes('contract')) {
        extracted = {
          shipmentType: 'document',
          pickup: SAVED_SHIPPERS[1] as AddressType,
          destination: SAVED_CONSIGNEES[0] as AddressType,
          pickupPinCode: SAVED_SHIPPERS[1].pincode,
          destinationPinCode: SAVED_CONSIGNEES[0].pincode,
          weight: 1.2,
          weightUnit: 'kg',
          hideBoxes: true,
          boxes: [],
        };
      } else {
        // Generic parcel extraction
        extracted = {
          shipmentType: 'parcel',
          pickup: SAVED_SHIPPERS[2] as AddressType,
          destination: SAVED_CONSIGNEES[2] as AddressType,
          pickupPinCode: SAVED_SHIPPERS[2].pincode,
          destinationPinCode: SAVED_CONSIGNEES[2].pincode,
          weight: 12.5,
          weightUnit: 'kg',
          hideBoxes: false,
          boxes: [
            {
              id: '0',
              weight: 12.5,
              length: 30,
              width: 30,
              height: 20,
              boxCopyCount: 0,
              startCount: 1,
              description: 'Industrial Spare Parts',
            },
          ] as any,
        };
      }

      resolve(extracted);
    }, 1500); // 1.5s simulated extraction time
  });
}
